import { createApi } from '@reduxjs/toolkit/query/react';
import { idType } from '@src/types';
import { baseQuerySample } from '@store/helpers';
import { format } from 'date-fns';

type filterType = {
  operator: string;
  value: {};
  values: Array<{}>;
};

export type getRidesByAssignedResType = {
  content: Array<{
    id: idType;
    contractor: string;
    dropOfCityZipcode: string;
    member: string;
    miles: number;
    pickUpCityZipcode: string;
    pickUpCounty: string;
    pickUpTime: string;
    rideOrderConfirmationCode: string;
    service: string;
    status: string;
    statusId: idType;
  }>;
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
  filter: {
    additionalProp1: filterType;
    additionalProp2: filterType;
    additionalProp3: filterType;
  };
  sortDir: 'asc' | 'desc';
  sortBy: string;
  table: string;
};

export type filterBodyTypes = {
  rideRequestDate?: string;
  startDate?: string;
  endDate?: string;
  state?: number;
  statusId?: Array<number>;
};

export type DriverFleetDriver = {
  driverUserId: number;
  /** Live fix on an active ride. When false, the ride fields are null and the position is the last known one. */
  online: boolean;
  /** False for a driver account with no location in the last 24 h: listed, but not on the map. */
  hasLocation: boolean;
  segmentId: number | null;
  rideId: number | null;
  rideOrderConfirmationCode: string | null;
  contractorId: number;
  contractorName: string | null;
  driverName: string | null;
  vehicle: string | null;
  statusId: number | null;
  status: string | null;
  headingTo: 'pickup' | 'dropoff' | null;
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  heading: number | null;
  /** Meters per second. */
  speed: number | null;
  recordedAt: string | null;
};

export type DriverFleetResType = {
  generatedAt: string;
  trackableSegments: number;
  /** Trackable segments with a live fix; the rest are rides whose driver isn't sharing location. */
  liveSegments: number;
  /** Every driver account: online first, then offline at the last known position (kept 24 h), then no location. */
  drivers: Array<DriverFleetDriver>;
};

export type SegmentTrackingResType = {
  segmentId: number;
  rideId: number;
  statusId: number | null;
  status: string | null;
  /** Complete / Canceled / Incomplete: the driver position is no longer returned. */
  finished: boolean;
  headingTo: 'pickup' | 'dropoff';
  pickupLatitude: number | null;
  pickupLongitude: number | null;
  dropoffLatitude: number | null;
  dropoffLongitude: number | null;
  driverLatitude: number | null;
  driverLongitude: number | null;
  driverHeading: number | null;
  /** Meters per second. */
  driverSpeed: number | null;
  driverRecordedAt: string | null;
};

export const monitoringApi = createApi({
  reducerPath: 'Monitoring',
  tagTypes: ['Monitoring'],
  baseQuery: baseQuerySample,
  endpoints: (builder) => ({
    getRidesByAssigned: builder.mutation<
      getRidesByAssignedResType,
      {
        body?: filterBodyTypes;
        params?: Record<string, string>;
      }
    >({
      query: ({ body = {}, params = {} }) => ({
        url: `/v1/search/ride?`,
        method: 'POST',
        body: {
          rideRequestDate: format(Date.now(), 'MM/dd/yyyy'),
          startDate: '00:00',
          endDate: '23:59',
          ...body,
        },
        params: {
          page: 0,
          size: 20,
          sortBy: 'rideDate',
          sortDir: 'desc',
          ...params,
        },
      }),
    }),
    getSegmentDetailsById: builder.query({
      query: ({ id, isPortalVersion = false }) => ({
        url: isPortalVersion
          ? `/v2/rides/assignment/contractor-portal/segment/${id}`
          : `/v2/rides/assignment/segment/${id}`,
      }),
    }),
    getDriverFleet: builder.query<DriverFleetResType, void>({
      query: () => ({ url: '/v1/rides/driver/location/fleet' }),
    }),
    getSegmentTracking: builder.query<SegmentTrackingResType, string>({
      query: (segmentId) => ({
        url: `/v1/rides/driver/location/segment/${segmentId}`,
      }),
    }),
  }),
});

export const {
  useGetRidesByAssignedMutation,
  useLazyGetSegmentDetailsByIdQuery,
  useGetDriverFleetQuery,
  useGetSegmentTrackingQuery,
} = monitoringApi;
